import { Fragment } from "react"
import { PlusCircle, SlidersHorizontal, X } from "lucide-react"
import type { Table } from "@tanstack/react-table"
import { Button } from "@components/ui/button"
import { TextFilter } from "@components/TextFilter"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@components/ui/dropdown-menu"
import { Badge } from "@components/ui/badge"
import { SelectDateRange } from "@components/SelectDateRange/SelectDateRange"
import {
  dateRangeToISO,
  isoToDateRange,
} from "@components/SelectDateRange/utils"
import { SelectFilter } from "@components/Filters/SelectFilter"
import { MultiSelectFilter } from "@components/Filters/MultiSelectFilter"
import { cn } from "@components/ui/lib/utils"
import type { TDateRangeISO } from "@components/SelectDateRange/types"
import type { TFilterConfig } from "./types"

type TServerDataTableToolbarProps<TData> = {
  table: Table<TData>
  filterConfig: TFilterConfig[]
  filterValues: Record<string, unknown>
  onFilterChange: (filters: Record<string, unknown>) => void
  onReset: () => void
}

function valuesAreDifferent(value: unknown, defaultValue: unknown): boolean {
  if (value === null || value === "" || value === undefined) return false

  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    if (value.length !== defaultValue.length) return true

    const sortedValue = [...value].sort()
    const sortedDefaultValues = [...defaultValue].sort()
    return !sortedValue.every((v, i) => v === sortedDefaultValues[i])
  }

  if (
    value &&
    defaultValue &&
    typeof value === "object" &&
    typeof defaultValue === "object" &&
    !Array.isArray(value) &&
    !Array.isArray(defaultValue)
  ) {
    const valueObj = value as Record<string, unknown>
    const defaultValueObj = defaultValue as Record<string, unknown>

    const valueKeys = Object.keys(valueObj)
    const defaultValueKeys = Object.keys(defaultValueObj)

    if (valueKeys.length !== defaultValueKeys.length) return true

    return valueKeys.some((k) => valueObj[k] !== defaultValueObj[k])
  }

  return value !== defaultValue
}

export function ServerDataTableToolbar<TData>({
  table,
  filterConfig,
  filterValues,
  onFilterChange,
  onReset,
}: TServerDataTableToolbarProps<TData>) {
  const isFiltered = Object.keys(filterValues).some((key) => {
    const filter = filterConfig.find((f) => f.id === key)
    const value = filterValues[key]

    return valuesAreDifferent(value, filter?.defaultValue)
  })

  const shouldUseMobileView = filterConfig.length > 5

  // Helper to get filter value by id
  const getFilterValue = (filterId: string) => {
    return filterValues[filterId]
  }

  // Helper to set filter value by id
  const setFilterValue = (filterId: string, value: unknown) => {
    const newValues = { ...filterValues }

    if (value === undefined || value === null || value === "") {
      delete newValues[filterId]
    } else {
      newValues[filterId] = value
    }

    onFilterChange(newValues)
    table.resetPageIndex()
  }

  // Render single-select filter
  const renderSingleSelectFilter = (filter: TFilterConfig) => {
    return (
      <SelectFilter
        key={filter.id}
        value={getFilterValue(filter.id) as string | null}
        onChange={(value) => setFilterValue(filter.id, value)}
        options={filter.options || []}
        placeholder={filter.placeholder || filter.id}
        className={cn(!shouldUseMobileView && "tw:md:w-fit")}
      />
    )
  }

  // Render date range filter
  const renderDateRangeFilter = (filter: TFilterConfig) => {
    const isoValue = getFilterValue(filter.id) as TDateRangeISO | undefined
    const dateRangeValue = isoToDateRange(isoValue)

    return (
      <SelectDateRange
        key={filter.id}
        value={dateRangeValue}
        onChange={(range) => {
          const isoRange = dateRangeToISO(range)
          setFilterValue(filter.id, isoRange)
        }}
        placeholder={filter.placeholder || filter.id}
        icon={PlusCircle}
        className={cn(!shouldUseMobileView && "tw:md:w-fit")}
      />
    )
  }

  // Render multi-select filter
  const renderMultiSelectFilter = (filter: TFilterConfig) => {
    return (
      <MultiSelectFilter
        key={filter.id}
        values={getFilterValue(filter.id) as Array<string | null>}
        onChange={(values) => setFilterValue(filter.id, values)}
        options={filter.options || []}
        placeholder={filter.placeholder || filter.id}
        className={cn(!shouldUseMobileView && "tw:md:w-fit")}
      />
    )
  }

  // Render text filter
  const renderTextFilter = (filter: TFilterConfig) => {
    return (
      <TextFilter
        key={filter.id}
        placeholder={filter.placeholder || `Filter ${filter.id}...`}
        initialValue={(getFilterValue(filter.id) as string) ?? ""}
        onChange={(value) => setFilterValue(filter.id, value)}
        debounceMs={500}
        className={cn(!shouldUseMobileView && "tw:md:w-fit")}
      />
    )
  }

  // Render filter based on type
  const renderFilter = (filter: TFilterConfig) => {
    switch (filter.type) {
      case "text":
        return renderTextFilter(filter)
      case "select":
        return renderSingleSelectFilter(filter)
      case "selectMultiple":
        return renderMultiSelectFilter(filter)
      case "dateRange":
        return renderDateRangeFilter(filter)
      default:
        return null
    }
  }

  const ResetFiltersButton = () => (
    <Button variant="ghost" size="sm" onClick={onReset}>
      Reset
      <X />
    </Button>
  )

  return (
    <>
      {/* Desktop View */}
      <div
        className={cn(
          "tw:hidden",
          !shouldUseMobileView && "tw:md:flex tw:md:flex-wrap tw:md:gap-2",
        )}
      >
        {filterConfig.map((filter) => renderFilter(filter))}
        {isFiltered && <ResetFiltersButton />}
      </div>

      {/* Mobile View */}
      <div className={cn("tw:flex", !shouldUseMobileView && "tw:md:hidden")}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <SlidersHorizontal />
              <span>
                Filters
                <Badge
                  variant={isFiltered ? "destructive" : "secondary"}
                  className="tw:ml-2"
                >
                  {Object.keys(filterValues).length}
                </Badge>
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="tw:flex tw:flex-col tw:gap-3 tw:p-3"
          >
            {filterConfig.map((filter, index) => (
              <Fragment key={filter.id}>
                <div className="tw:flex tw:items-center">
                  {renderFilter(filter)}
                </div>
                {index < filterConfig.length - 1 && (
                  <DropdownMenuSeparator className="tw:m-0" />
                )}
              </Fragment>
            ))}

            {isFiltered && (
              <>
                <DropdownMenuSeparator />
                <div className="tw:flex tw:items-center tw:justify-center">
                  <ResetFiltersButton />
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}
